import moment from 'moment-timezone';
import { PureComponent } from 'react';
import { batch } from 'react-redux';

import { EXTEND_CONFERENCE_EXPIRY, setConferenceExpired } from '../../base/conference';
import { disconnect } from '../../base/connection';
import { openDialog } from '../../base/dialog';
import { clearNotifications, showNotification } from '../../notifications';
import { AlertBox } from '../../toolbox/components/web/AlertBox';

/**
 * Props of MeetingCountdown.
 */
type Props = {

    /**
     * Meeting end time in string: "YYYY-MM-DD HH:mm:ss"
     */
    meetingEndTime: string,

    /**
     * Meeting timezone example: "Asia/Kolkata"
     */
    meetingTimezone: string,

    /**
     * Redux dispatcher
     */
    dispatch: Function,

    /**
     * Whether conference has joined
     */
    _mhConferenceJoined: boolean,

    /**
     * Conference expired flag
     */
    conferenceExpired?: boolean,
};

/**
 * State of MeetingCountdown.
 */
type State = {
    timerValue: string,
    disableNotification: boolean,
    hasShownExpiringNotification: boolean
};

/**
 * Meeting countdown until end time.
 */
class AbstractMeetingCountdown extends PureComponent<Props, State> {

    _interval: ?IntervalID;

    /**
     * Initializes a new MeetingCountdown instance.
     * @param {*} props
     */
    constructor(props: Props) {
        super(props);
        this.state = {
            timerValue: '00:00:00',
            disableNotification: false
        };
    }

    /**
     * React lifecycle method invoked after the component is mounted.
     */
    componentDidMount() {
        this._startCountdown();
    }

    /**
     * React lifecycle method invoked after the component updates.
     */
    componentDidUpdate(prevProps: Props) {
        if ((!prevProps.meetingEndTime && this.props.meetingEndTime)
            || (!prevProps.meetingTimezone && this.props.meetingTimezone)) {
            this._startCountdown();
        }
    }

    /**
     * React lifecycle method invoked before the component is unmounted.
     */
    componentWillUnmount() {
        this._stopCountdown();
    }

    /**
     * Calculate countdown — using your REQUIRED date pattern
     */
    _calculateCountdown = () => {
        const { meetingEndTime, meetingTimezone, extendTime } = this.props;

        if (!meetingEndTime || !meetingTimezone) {
            return {
                expired: false,
                aboutToExpire: false,
                minutesLeft: 0,
                formatted: '00:00:00'
            };
        }

        const now = moment.tz(meetingTimezone);
        const end = moment.tz(meetingEndTime, 'YYYY-MM-DD HH:mm:ss', meetingTimezone);
        const diffMs = extendTime ? end.diff(now) + 600000 : end.diff(now);
        const minutesLeft = Math.floor(diffMs / 60000);

        if (diffMs <= 0) {
            return {
                expired: true,
                aboutToExpire: false,
                minutesLeft: 0,
                formatted: '00:00:00'
            };
        }

        const duration = moment.duration(diffMs);

        const formattedParts = [];

        if (duration.days() > 0) {
            formattedParts.push(String(duration.days()).padStart(2, '0'));
        }

        formattedParts.push(
            String(duration.hours()).padStart(2, '0'),
            String(duration.minutes()).padStart(2, '0'),
            String(duration.seconds()).padStart(2, '0')
        );

        const formatted = formattedParts.join(':');

        return {
            expired: false,
            aboutToExpire: minutesLeft < 10 && minutesLeft > 0,
            minutesLeft,
            formatted
        };
    };

    /**
     * Start countdown.
     */
    _startCountdown = () => {
        const { meetingEndTime, meetingTimezone } = this.props;

        if (!meetingEndTime || !meetingTimezone) {
            return;
        }

        if (!this._interval) {
            this._updateCountdown();
            this._interval = setInterval(() => this._updateCountdown(), 1000);
        }
    };

    /**
     * Update countdown value.
     */
    _updateCountdown = () => {
        const { dispatch, _mhConferenceJoined, hasShownExpiringNotification, extendExpiry, extendTime, conference } = this.props;
        const result = this._calculateCountdown();

        if (result.aboutToExpire && !hasShownExpiringNotification && _mhConferenceJoined) {
            if (extendExpiry && !extendTime && this.props.isModerator) {
                dispatch(showNotification({
                    titleKey: 'notify.meetingExpiredTitle',
                    descriptionKey: 'notify.meetingExpiredMessage',
                    customActionNameKey: 'notify.extendMeeting',
                    customActionHandler: () => batch(() => {
                        conference.sendMessage({
                            type: EXTEND_CONFERENCE_EXPIRY,
                            extendConferenceExpiry: true });
                        dispatch(clearNotifications());
                    })
                }));
            } else {
                dispatch(showNotification({
                    titleKey: 'notify.meetingExpiredTitle',
                    descriptionKey: 'notify.meetingExpiredMessage' }));
            }

            this.props.onExpiringNotificationShown(true);
        }

        if (hasShownExpiringNotification && !result.aboutToExpire) {
            this.props.onExpiringNotificationShown(false);
        }

        if (result.expired && _mhConferenceJoined) {
            dispatch(setConferenceExpired());
            dispatch(disconnect(false));

            dispatch(openDialog(AlertBox, {
                alertText: 'notify.meetingExpireDialog',
                disableOverlay: true,
                submitDisabled: false,
                titleKey: 'notify.meetingExpireDialogTitle',
                cancelDisabled: true
            }));


            this._stopCountdown();

            return;
        }

        this.setState({ timerValue: result.formatted });
    };

    /**
     * Stop countdown.
     */
    _stopCountdown = () => {
        if (this._interval) {
            clearInterval(this._interval);
            this._interval = null;
        }
        this.setState({ timerValue: '00:00:00' });
    };
}

export default AbstractMeetingCountdown;
